import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  NavigateBefore as PrevPageIcon,
  NavigateNext as NextPageIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

interface PDFViewerProps {
  storageUrl: string;
  documentTitle: string;
  onPageChange?: (page: number) => void;
  language?: 'English' | 'Hebrew';
  highlightPage?: number;  // Page to scroll to (1-indexed)
  highlightBbox?: number[];  // Normalized coords [x0, y0, x1, y1] (0-1)
}

export default function PDFViewer({ 
  storageUrl, 
  documentTitle, 
  onPageChange,
  language = 'English',
  highlightPage,
  highlightBbox
}: PDFViewerProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [zoom, setZoom] = useState(100);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showHighlight, setShowHighlight] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const fontFamily = language === 'Hebrew' 
    ? 'Segoe UI, Tahoma, Geneva, Verdana, Arial, sans-serif, "Segoe UI Hebrew", "Noto Sans Hebrew"'
    : 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif';

  // Load PDF page count
  useEffect(() => {
    const loadPdfPageCount = async () => {
      if (!storageUrl) return;

      try {
        // Load PDF.js from CDN if not already loaded
        if (!(window as any).pdfjsLib) {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
          script.async = true;
          
          await new Promise((resolve, reject) => {
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });

          // Set worker
          (window as any).pdfjsLib.GlobalWorkerOptions.workerSrc = 
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        }

        // Load the PDF to get page count
        const loadingTask = (window as any).pdfjsLib.getDocument(storageUrl);
        const pdf = await loadingTask.promise;
        
        console.log('📄 PDF loaded:', pdf.numPages, 'pages');
        setTotalPages(pdf.numPages);
      } catch (error) {
        console.error('❌ Error loading PDF page count:', error);
        // Keep default totalPages = 1 on error
      }
    };

    loadPdfPageCount();
  }, [storageUrl]);

  // Reset viewer when document changes
  useEffect(() => {
    setCurrentPage(highlightPage || 1);
    setZoom(100);
    setIsLoading(true);
    setError(null);
    // Show highlight notification when navigating from search
    if (highlightPage) {
      console.log('🔦 [PDFViewer] Highlight requested - page:', highlightPage, 'bbox:', highlightBbox);
      setShowHighlight(true);
      // Auto-hide after 8 seconds
      const timer = setTimeout(() => setShowHighlight(false), 8000);
      return () => clearTimeout(timer);
    } else {
      setShowHighlight(false);
    }
  }, [storageUrl, highlightPage, highlightBbox]);
  
  // Build PDF URL with page navigation
  const getPdfUrl = () => {
    if (!storageUrl) return '';
    // Add #page=N to navigate to specific page (works with most PDF viewers)
    const pageParam = highlightPage ? `#page=${highlightPage}` : '';
    return `${storageUrl}${pageParam}`;
  };

  const handleZoomChange = (newZoom: number) => {
    setZoom(newZoom);
    // Note: We can't directly control iframe zoom due to cross-origin restrictions
    // The zoom state is maintained for UI display purposes
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      onPageChange?.(newPage);
      // Note: We can't directly control iframe page navigation due to cross-origin restrictions
      // The page state is maintained for UI display purposes
    }
  };

  const resetViewer = () => {
    setCurrentPage(1);
    setZoom(100);
    // Reload the iframe to reset to first page
    if (iframeRef.current) {
      const currentSrc = iframeRef.current.src;
      iframeRef.current.src = '';
      setTimeout(() => {
        if (iframeRef.current) {
          iframeRef.current.src = currentSrc;
        }
      }, 100);
    }
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
    // Note: We can't access iframe content due to cross-origin restrictions
    // totalPages is now loaded via PDF.js in separate useEffect
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setError('Failed to load PDF');
  };

  return (
    <Paper sx={{ 
      p: 2, 
      borderRadius: 3, 
      boxShadow: 2, 
      bgcolor: '#fff', 
      width: '100%' 
    }}>
      {/* PDF Controls */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        mb: 2,
        flexWrap: 'wrap',
        gap: 1
      }}>
        {/* Page Navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={language === 'Hebrew' ? 'עמוד ראשון' : 'First Page'}>
            <IconButton
              size="small"
              onClick={() => handlePageChange(1)}
              disabled={currentPage <= 1}
            >
              <FirstPageIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title={language === 'Hebrew' ? 'עמוד קודם' : 'Previous Page'}>
            <IconButton
              size="small"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              <PrevPageIcon />
            </IconButton>
          </Tooltip>
          
          <Typography sx={{ 
            fontFamily, 
            fontSize: '0.9rem',
            minWidth: '80px',
            textAlign: 'center'
          }}>
            {language === 'Hebrew' ? 'עמוד' : 'Page'} {currentPage} {language === 'Hebrew' ? 'מתוך' : 'of'} {totalPages}
          </Typography>
          
          <Tooltip title={language === 'Hebrew' ? 'עמוד הבא' : 'Next Page'}>
            <IconButton
              size="small"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              <NextPageIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title={language === 'Hebrew' ? 'עמוד אחרון' : 'Last Page'}>
            <IconButton
              size="small"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage >= totalPages}
            >
              <LastPageIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Zoom Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={language === 'Hebrew' ? 'הקטן' : 'Zoom Out'}>
            <IconButton
              size="small"
              onClick={() => handleZoomChange(Math.max(50, zoom - 25))}
              disabled={zoom <= 50}
            >
              <ZoomOutIcon />
            </IconButton>
          </Tooltip>
          
          <Typography sx={{ 
            fontFamily, 
            fontSize: '0.9rem',
            minWidth: '60px',
            textAlign: 'center'
          }}>
            {zoom}%
          </Typography>
          
          <Tooltip title={language === 'Hebrew' ? 'הגדל' : 'Zoom In'}>
            <IconButton
              size="small"
              onClick={() => handleZoomChange(Math.min(200, zoom + 25))}
              disabled={zoom >= 200}
            >
              <ZoomInIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Reset Button */}
        <Tooltip title={language === 'Hebrew' ? 'אפס תצוגה' : 'Reset View'}>
          <IconButton
            size="small"
            onClick={resetViewer}
            sx={{ color: '#2B3A67' }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* PDF Viewer */}
      <Box sx={{ 
        position: 'relative',
        width: '100%',
        height: '70vh',
        border: '1px solid #2B3A67',
        borderRadius: 2,
        overflow: 'hidden'
      }}>
        {isLoading && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            zIndex: 1
          }}>
            <CircularProgress size={40} />
            <Typography sx={{ fontFamily, mt: 2 }}>
              {language === 'Hebrew' ? 'טוען PDF...' : 'Loading PDF...'}
            </Typography>
          </Box>
        )}
        
        {error && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#fff',
            zIndex: 1
          }}>
            <Typography sx={{ fontFamily, color: 'error.main' }}>
              {error}
            </Typography>
          </Box>
        )}
        
        <iframe
          key={`pdf-page-${currentPage}`}
          ref={iframeRef}
          src={getPdfUrl()}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            display: isLoading || error ? 'none' : 'block'
          }}
          title="PDF Preview"
          onLoad={handleIframeLoad}
          onError={handleIframeError}
        />
        
        {/* Highlight Banner - shows when navigating from search */}
        {showHighlight && highlightPage && (
          <Box
            onClick={() => setShowHighlight(false)}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bgcolor: '#FFF3CD',
              borderBottom: '3px solid #FFD700',
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              zIndex: 20,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              animation: 'slideDown 0.3s ease-out',
              '@keyframes slideDown': {
                '0%': { transform: 'translateY(-100%)' },
                '100%': { transform: 'translateY(0)' }
              }
            }}
          >
            <Box sx={{ 
              width: 24, 
              height: 24, 
              borderRadius: '50%', 
              bgcolor: '#FFD700', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              fontSize: '14px'
            }}>
              🔍
            </Box>
            <Typography sx={{ fontFamily, fontWeight: 600, color: '#856404' }}>
              {language === 'Hebrew' 
                ? `מידע רלוונטי נמצא בעמוד ${highlightPage} - גלול מטה כדי לראות`
                : `Relevant content found on page ${highlightPage} - scroll down to view`
              }
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#666' }}>
              {language === 'Hebrew' ? '(לחץ לסגירה)' : '(click to dismiss)'}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
} 